import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { BUTTON_LABELS } from '../../constants/buttonLabels';
import { useModalState } from '../../hooks/useModalState';
import { useOrderCancelMutation } from '../../hooks/useOrderCancelMutation';
import { useFlattenedOrders, useOpenOrders } from '../../hooks/useOrders';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { isUserRejectedError } from '../../lib/errors';
import { localStorageKeys } from '../../lib/localstorageKeys';
import { Alert } from '../Alert';
import { Button } from '../catalyst-ui-kit/button';
import { Checkbox, CheckboxField } from '../catalyst-ui-kit/checkbox';
import { Label } from '../catalyst-ui-kit/fieldset';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';

export type CancelAllOrdersModalUiProps = {
  isOpen: boolean;
  hasOrders: boolean;
  isPending: boolean;
  hasError: boolean;
  showModal: boolean;
  onClose: () => void;
  setShowModal: (value: boolean) => void;
  handleCancelAll: () => Promise<void>;
};

export const CancelAllOrdersModalUi: React.FC<CancelAllOrdersModalUiProps> = ({
  isOpen,
  onClose,
  hasOrders,
  isPending,
  hasError,
  showModal,
  setShowModal,
  handleCancelAll,
}) => {
  return (
    <ResponsiveModal onClose={onClose} open={isOpen} title="Cancel All Open Orders">
      <div className="flex flex-col gap-6">
        <p className="text-xs font-medium text-contentSecondary">
          This action will cancel all open orders.
        </p>

        {hasError && (
          <Alert variant="error">
            <p>Failed to cancel orders. Please try again.</p>
          </Alert>
        )}

        <CheckboxField>
          <Checkbox
            id="disable-modal-checkbox"
            color="gray"
            checked={!showModal}
            onChange={(value) => setShowModal(!value)}
            className="cursor-pointer"
          />
          <Label
            htmlFor="disable-modal-checkbox"
            className="cursor-pointer text-3xs font-normal text-contentSecondary"
          >
            Do not show this confirmation modal anymore
          </Label>
        </CheckboxField>

        <div className="grid grid-cols-2 gap-2">
          <Button color="tertiary" onClick={onClose} disabled={isPending}>
            {BUTTON_LABELS.action.cancel}
          </Button>
          <Button color="blue" disabled={!hasOrders || isPending} onClick={handleCancelAll}>
            {hasError
              ? BUTTON_LABELS.action.tryAgain
              : isPending
                ? BUTTON_LABELS.state.cancelling
                : BUTTON_LABELS.action.confirm}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export const CancelAllOrdersModal: React.FC = () => {
  const cancelAllOrdersModal = useModalState((state) => state.cancelAllOrdersModal);
  const setCancelAllOrdersModal = useModalState((state) => state.setCancelAllOrdersModal);
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: orderData } = useOpenOrders({
    subaccountId: selectedSubaccount?.id,
  });
  const [showModal, setShowModal] = useLocalStorage(
    localStorageKeys.showConfirmCancelAllModal,
    true
  );

  const [pendingShowModal, setPendingShowModal] = useState(showModal);

  const cancelMutation = useOrderCancelMutation();

  const closeModal = useCallback(() => {
    setCancelAllOrdersModal('closed');
    setPendingShowModal(showModal);
    cancelMutation.reset();

    if (pendingShowModal !== showModal) {
      setShowModal(pendingShowModal);
    }
  }, [setCancelAllOrdersModal, showModal, cancelMutation, pendingShowModal, setShowModal]);

  const orders = useFlattenedOrders(orderData?.pages);
  const handleCancelAll = useCallback(async () => {
    if (!orders || orders.length === 0) return;
    const orderIds = orders.map((order) => order.id);

    try {
      // TODO The backend might add a cancel all orders endpoint we can use
      // Otherwise we should consider calling `fetchNextPage` from useOpenOrders
      await cancelMutation.mutateAsync(orderIds);

      closeModal();
    } catch (error) {
      if (!isUserRejectedError(error)) {
        console.error('Error cancelling all orders:', error);
      }
    }
  }, [orders, cancelMutation, closeModal]);

  // If the user has disabled confirmation modals, call handleCancelAll without showing the modal
  useEffect(() => {
    if (cancelAllOrdersModal === 'open' && !showModal) {
      handleCancelAll().catch(console.error).finally(closeModal);
    }
  }, [cancelAllOrdersModal, showModal, closeModal, handleCancelAll, setCancelAllOrdersModal]);

  return (
    <CancelAllOrdersModalUi
      isOpen={cancelAllOrdersModal === 'open' && showModal}
      onClose={closeModal}
      hasOrders={orders !== undefined && orders.length > 0}
      isPending={cancelMutation.isPending}
      hasError={cancelMutation.isError}
      showModal={pendingShowModal}
      setShowModal={setPendingShowModal}
      handleCancelAll={handleCancelAll}
    />
  );
};
